// Owner: design-system (plan §9.1, owner feedback F3). The workspace brand in the app itself: the
// accent (buttons, links, selection, focus) in both themes, the mark as the browser-tab icon and in
// the rail's workspace switcher. Public surfaces that look like the app (sign-in, forms) apply the same
// accent sheet while they are shown (`usePublicAccent`).
import { useEffect, useLayoutEffect } from 'react';
import { HEX_RE } from '@dataroom/core/doc/color';
import { brandAppAccent } from '@dataroom/core/doc/theme';
import { useMe } from '../../lib/queries';
import { applyBrandAccent } from './accent';
import { applyFavicon, rememberBrand } from './favicon';
import { useBrandKit } from './api';

const CACHE_PREFIX = 'dr.brand.accent.';

/** The accent this browser kept for the workspace (it goes into a style sheet: only a hex colour reads back). */
function cachedAccent(workspaceId: string): string | null {
  try {
    const stored = localStorage.getItem(CACHE_PREFIX + workspaceId);
    return stored && HEX_RE.test(stored) ? stored : null;
  } catch {
    return null;
  }
}

function cacheAccent(workspaceId: string, hex: string | null) {
  try {
    if (hex) localStorage.setItem(CACHE_PREFIX + workspaceId, hex);
    else localStorage.removeItem(CACHE_PREFIX + workspaceId);
  } catch {
    // Storage unavailable: the accent applies once the kit loads.
  }
}

/**
 * The workspace brand in an authenticated layout (app, settings, focus): the kit's accent (when
 * `app.accent` is on) repaints the accent tokens, the mark becomes the tab icon. The last accent is
 * remembered per workspace so the next load starts in it without a flash of the product accent.
 * Apply and clean-up are layout effects, so moving between layouts never drops the accent for a frame.
 *
 *   useAppBrand();
 */
export function useAppBrand(): void {
  const workspaceId = useMe().workspace.id;
  const kit = useBrandKit();
  const accent = kit.data ? brandAppAccent(kit.data.kit) : cachedAccent(workspaceId);
  const mark = kit.data?.logos.mark?.url ?? null;
  useLayoutEffect(() => {
    applyBrandAccent(accent);
    // Leaving the app frame (sign-out, public pages): the product accent again.
    return () => applyBrandAccent(null);
  }, [accent]);
  useLayoutEffect(() => {
    applyFavicon(mark);
    return () => applyFavicon(null);
  }, [mark]);
  useEffect(() => {
    if (!kit.data) return;
    const next = brandAppAccent(kit.data.kit);
    cacheAccent(workspaceId, next);
    rememberBrand({ accent: next, mark: kit.data.logos.mark?.url ?? null });
  }, [kit.data, workspaceId]);
}
